import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Handle redirect when user becomes available
  useEffect(() => {
    if (user) {
      console.log('User detected in login page, redirecting...');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Clear error when user starts typing
  useEffect(() => {
    if (error && (email || password)) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000); // Clear error after 3 seconds when user is typing

      return () => clearTimeout(timer);
    }
  }, [email, password, error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      console.log('Attempting login...');

      await login(email, password);
      console.log('Login successful, navigating to dashboard...');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Login failed:', err);
      // Extract error message from different possible error structures
      let errorMessage = 'Login failed. Please try again.';

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      // Don't navigate on error - keep user on login page
      // Explicitly prevent any further propagation
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="w-full  sm:max-w-lg lg:max-w-xl space-y-6 sm:space-y-8">
        {/* Header - Mobile optimized */}
        <div className="text-center space-y-2 sm:space-y-3">
          <h2 className="text-4xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">
            Welcome Back
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-medium">
            Attendance Management System
          </p>
          <p className="text-sm sm:text-base text-gray-500">
            Sign in to continue to your account
          </p>
        </div>

        {/* Card - Responsive padding and spacing */}
        <Card className="shadow-xl sm:shadow-2xl border-0 sm:border">
          <CardHeader className="space-y-2 pb-4 sm:pb-6 px-4 sm:px-6 pt-6 sm:pt-8">
            <CardTitle className="text-2xl sm:text-3xl font-bold text-center sm:text-left">Login</CardTitle>
            <CardDescription className="text-base sm:text-sm text-center sm:text-left">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-6 sm:pb-8">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-4" noValidate>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-3 sm:p-4 rounded-md animate-shake">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 sm:h-5 sm:w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-base sm:text-sm text-red-700 font-medium">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="block text-base sm:text-sm font-semibold sm:font-medium text-gray-700">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(''); // Clear error when user types
                  }}
                  required
                  className="h-14 sm:h-11 text-base sm:text-sm rounded-lg sm:rounded-md border-2 sm:border focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="block text-base sm:text-sm font-semibold sm:font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError(''); // Clear error when user types
                    }}
                    required
                    placeholder="Enter your password"
                    className="pr-12 h-14 sm:h-11 text-base sm:text-sm rounded-lg sm:rounded-md border-2 sm:border focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 sm:pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                    ) : (
                      <EyeIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-14 sm:h-11 text-lg sm:text-base font-semibold rounded-lg sm:rounded-md shadow-lg sm:shadow-md hover:shadow-xl sm:hover:shadow-lg transition-all"
                onClick={(e) => {
                  // Prevent any potential double submission
                  if (loading) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
